import {
  Stack,
  IconButton,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';

import { CommingSoonDialog, Iconify } from '@app/components';
import { useDialog, useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { useAuthRolePermissions } from '@app/utils';
import { AddTimeLimitPopup } from './add-time-limit-popup';
import { AddLabelPopup } from './add-label-popup';
import { addAttachment, deleteCardByID } from '../slice';
import {
  selectAddAttachmentState,
  selectCardDetailState,
  selectDeleleteCardState,
} from '../selector';
import { AddMemberPopover } from './add-member-popup';
import { DeletePopover } from './delete-popover';
import { IDeleteCardSchema } from '../types';
import { AddCheckListPopover } from './add-checklist-popup';

interface Props {
  ids: IDeleteCardSchema;
  onClose?: () => void;
  isLoggedInUser: any;
}

const SideBar = ({ ids, onClose, isLoggedInUser }: Props) => {
  const methods = useForm();
  const theme = useTheme();
  const dispatch = useDispatch();
  const comingSoon = useDialog();

  const [openTimeLimitPopup, setOpenTimeLimitPopup] = useState<HTMLButtonElement | null>(null);
  const [openLabelPopup, setOpenLabelPopup] = useState<HTMLButtonElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openDeletePopup, setOpenDeletePopup] = useState<HTMLButtonElement | null>(null);
  const [openCheckList, setOpenCheckList] = useState<HTMLButtonElement | null>(null);
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleOpenDatePicker = (event: React.MouseEvent<HTMLDivElement>) => {
    setOpenTimeLimitPopup(event.currentTarget as any);
  };

  const handleOpenLabelPopup = (event: React.MouseEvent<HTMLDivElement>) => {
    setOpenLabelPopup(event.currentTarget as any);
  };

  useRequestState({
    stateSelector: selectAddAttachmentState,
    errorShown: true,
  });

  useRequestState({
    stateSelector: selectDeleleteCardState,
    onSuccess: onClose,
    errorShown: true,
  });

  const handleFileUpload = (files: File[]) => {
    const newFiles = files?.map((file: any) =>
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );

    if (newFiles.length > 0) {
      const existingAttachments = methods.getValues('attachment') || [];
      const allAttachments = [...existingAttachments, ...newFiles];
      methods.setValue('attachment', allAttachments, { shouldValidate: true });
      setSelectedFiles(allAttachments);
    }
  };

  const handleSubmitAttachment = () => {
    if (selectedFiles.length > 0) {
      dispatch(
        addAttachment({
          attachments: selectedFiles,
          cardId: ids?.cardId,
        })
      );
    }
    handleCloseAttachmentDialog();
  };

  const handleCloseAttachmentDialog = () => {
    setOpenAttachmentDialog(false);
    setSelectedFiles([]);
    methods.setValue('attachment', []);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget as any);
  };

  const handleCloseDeletePopup = () => {
    setOpenDeletePopup(null);
  };

  const open = Boolean(anchorEl);

  const handleCloseDatePicker = () => {
    setOpenTimeLimitPopup(null);
  };

  const handleCloseLabelPopup = () => {
    setOpenLabelPopup(null);
  };

  const handleDeleteDialog = (event: React.MouseEvent<HTMLDivElement>) => {
    setOpenDeletePopup(event.currentTarget as any);
  };

  const handleDelete = () => {
    dispatch(deleteCardByID(ids));
    onClose?.();
  };

  const { data } = useRequestState({
    stateSelector: selectCardDetailState,
  });

  const handleOpenChecklist = (event: React.MouseEvent<HTMLDivElement>) => {
    setOpenCheckList(event.currentTarget as any);
  };

  const handleCloseChecklist = () => {
    setOpenCheckList(null);
  };

  const handleOpenAttachmentDialog = () => {
    setOpenAttachmentDialog(true);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
  });

  const { hasPermission } = useAuthRolePermissions();
  const canAddAttachment = hasPermission('Attachments', 'w');
  const canAddCardMember = hasPermission('Card Members', 'w');
  const canAddLabels = hasPermission('Labels', 'w');
  const canDeleteCards = hasPermission('Cards', 'w');
  const canAddDates = hasPermission('Dates', 'w');
  const canAddCheckList = hasPermission('Checklist', 'w');

  return (
    <>
      <Stack sx={{ width: '30%' }} mt={4} ml={0}>
        <Stack
          onClick={canAddAttachment ? handleOpenAttachmentDialog : undefined}
          sx={{
            flexDirection: 'row',
            backgroundColor: theme.palette.grey[300],
            alignItems: 'center',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          mx={1}
        >
          <IconButton aria-label="close" sx={{ color: theme.palette.common.black }}>
            <Iconify icon={'typcn:attachment'} />
          </IconButton>
          <span>{Strings.taskBoard.attachment}</span>
        </Stack>
        <Stack
          onClick={canAddCardMember ? handleClick : undefined}
          sx={{
            flexDirection: 'row',
            backgroundColor: theme.palette.grey[300],
            alignItems: 'center',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          mx={1}
          mt={1}
        >
          <IconButton aria-label="close" sx={{ color: theme.palette.common.black }}>
            <Iconify icon={'healthicons:ui-user-profile'} />
          </IconButton>
          <Typography>{Strings.taskBoard.members}</Typography>
        </Stack>
        <Stack
          onClick={canAddLabels ? handleOpenLabelPopup : undefined}
          sx={{
            flexDirection: 'row',
            backgroundColor: theme.palette.grey[300],
            alignItems: 'center',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          mx={1}
          mt={1}
        >
          <IconButton aria-label="close" sx={{ color: theme.palette.common.black }}>
            <Iconify icon={'pajamas:label'} />
          </IconButton>
          <Typography>{Strings.taskBoard.labels}</Typography>
        </Stack>
        <Stack
          onClick={canAddCheckList ? handleOpenChecklist : undefined}
          sx={{
            flexDirection: 'row',
            backgroundColor: theme.palette.grey[300],
            alignItems: 'center',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          mx={1}
          mt={1}
        >
          <IconButton aria-label="close" sx={{ color: theme.palette.common.black }}>
            <Iconify icon={'mdi:checkbox-marked-outline'} />
          </IconButton>
          <Typography>{Strings.taskBoard.checklist}</Typography>
        </Stack>
        <Stack
          onClick={canDeleteCards ? handleDeleteDialog : undefined}
          sx={{
            flexDirection: 'row',
            backgroundColor: theme.palette.grey[300],
            alignItems: 'center',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          mx={1}
          mt={1}
        >
          <IconButton aria-label="close" sx={{ color: theme.palette.common.black }}>
            <Iconify icon={'mi:delete'} />
          </IconButton>
          <Typography>{Strings.taskBoard.delete}</Typography>
        </Stack>
        <Stack
          onClick={canAddDates ? handleOpenDatePicker : undefined}
          sx={{
            flexDirection: 'row',
            backgroundColor: theme.palette.grey[300],
            alignItems: 'center',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          mx={1}
          mt={1}
        >
          <IconButton aria-label="close" sx={{ color: theme.palette.common.black }}>
            <Iconify icon={'il:clock'} />
          </IconButton>
          <Typography>{Strings.taskBoard.dates}</Typography>
        </Stack>
        <AddTimeLimitPopup
          openTimeLimitPopup={openTimeLimitPopup}
          handleCloseDatePicker={handleCloseDatePicker}
          cardId={ids?.cardId}
          onClose={onClose}
        />
        <AddLabelPopup
          openLabelPopup={openLabelPopup}
          handleCloseLabelPopup={handleCloseLabelPopup}
        />
        <AddMemberPopover
          open={open}
          anchorEl={anchorEl}
          handleClose={handleClose}
          cardId={Number(ids?.cardId)}
        />
        <AddCheckListPopover
          open={openCheckList}
          handleClose={handleCloseChecklist}
          cardId={data?.id}
        />
        <DeletePopover
          openDeletePopup={openDeletePopup}
          handleCloseDeletePopup={handleCloseDeletePopup}
          handleDelete={handleDelete}
          content={Strings.taskBoard.deleteContentOfCard}
          heading={Strings.taskBoard.deleteHeading}
        />
        <Dialog
          open={openAttachmentDialog}
          onClose={handleCloseAttachmentDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{Strings.common.attachFiles}</DialogTitle>
          <DialogContent>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #aaa',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: isDragActive
                  ? theme.palette.grey[300]
                  : theme.palette.common.white,
                cursor: 'pointer',
              }}
            >
              <input {...getInputProps()} />
              <Typography>
                {selectedFiles.length > 0
                  ? selectedFiles.map((file) => file.name).join(', ')
                  : isDragActive
                  ? Strings.taskBoard.dropTheFilesHere
                  : Strings.taskBoard.dragAndDrop}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseAttachmentDialog} color="error">
              {Strings.button.cancel}
            </Button>
            {selectedFiles.length > 0 && (
              <Button onClick={handleSubmitAttachment} variant="contained">
                {Strings.button.submit}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {comingSoon.visible && <CommingSoonDialog open onClose={comingSoon.hide} />}
      </Stack>
    </>
  );
};

export { SideBar };
